import type { AnalysisBuffer } from '../types/image.js';
import type { NormalizedRect } from '../types/geometry.js';
import type { RoiRegionDetail, RoiSharpness, SharpnessMetrics } from '../types/metrics.js';
import { clamp } from '../types/geometry.js';
import { mean, variance } from '../geometry/statistics.js';

/**
 * Sharpness / blur indicators.
 *
 * Two layers live here:
 *
 *  - `computeSharpness` — the classic whole-frame numbers (Laplacian variance,
 *    Tenengrad, high-frequency ratio). They are computed on the *analysis*
 *    buffer (long side <= 720 px) and stay in the report so an existing
 *    threshold can be re-tuned without touching code. They are, however, not
 *    comparable between two frames where the face occupies a different share
 *    of the image, which is exactly why the product saw false "blurred" fails.
 *
 *  - `computeRoiSharpness` — the face-aware replacement. Each region of
 *    interest is resampled to a fixed reference width and only then measured,
 *    so the score is invariant to the analysis scale and to the face size.
 *    Several independent metrics are mapped smoothly into 0..1 and combined;
 *    the eyes carry the most weight because they are the most detail-rich,
 *    best-localised region of a portrait.
 *
 * Design rule: this module never throws. A broken buffer, a degenerate ROI or
 * a ROI that lies outside the image degrades to a low score plus a machine
 * readable note instead of an exception.
 */

// ---------------------------------------------------------------------------
// Whole-frame metrics (kept for backwards compatibility and debugging)
// ---------------------------------------------------------------------------

const laplacianAt = (
  luma: Uint8ClampedArray,
  width: number,
  height: number,
  x: number,
  y: number,
): number => {
  const xm = x > 0 ? x - 1 : 0;
  const xp = x < width - 1 ? x + 1 : width - 1;
  const ym = y > 0 ? y - 1 : 0;
  const yp = y < height - 1 ? y + 1 : height - 1;
  const centre = luma[y * width + x] as number;
  const up = luma[ym * width + x] as number;
  const down = luma[yp * width + x] as number;
  const left = luma[y * width + xm] as number;
  const right = luma[y * width + xp] as number;
  return up + down + left + right - 4 * centre;
};

const sobelMagnitudeAt = (
  luma: Uint8ClampedArray,
  width: number,
  height: number,
  x: number,
  y: number,
): number => {
  const xm = x > 0 ? x - 1 : 0;
  const xp = x < width - 1 ? x + 1 : width - 1;
  const ym = y > 0 ? y - 1 : 0;
  const yp = y < height - 1 ? y + 1 : height - 1;
  const tl = luma[ym * width + xm] as number;
  const tc = luma[ym * width + x] as number;
  const tr = luma[ym * width + xp] as number;
  const ml = luma[y * width + xm] as number;
  const mr = luma[y * width + xp] as number;
  const bl = luma[yp * width + xm] as number;
  const bc = luma[yp * width + x] as number;
  const br = luma[yp * width + xp] as number;
  const gx = tr + 2 * mr + br - (tl + 2 * ml + bl);
  const gy = bl + 2 * bc + br - (tl + 2 * tc + tr);
  return Math.hypot(gx, gy) / 4;
};

/** 3x3 box blur, used as the low-pass reference for the high-frequency ratio. */
const boxBlur3 = (luma: ArrayLike<number>, width: number, height: number): Float32Array => {
  const out = new Float32Array(width * height);
  for (let y = 0; y < height; y += 1) {
    const ym = y > 0 ? y - 1 : 0;
    const yp = y < height - 1 ? y + 1 : height - 1;
    for (let x = 0; x < width; x += 1) {
      const xm = x > 0 ? x - 1 : 0;
      const xp = x < width - 1 ? x + 1 : width - 1;
      let sum = 0;
      for (let yy = ym; yy <= yp; yy += 1) {
        for (let xx = xm; xx <= xp; xx += 1) {
          sum += luma[yy * width + xx] as number;
        }
      }
      out[y * width + x] = sum / 9;
    }
  }
  return out;
};

export const computeSharpness = (buffer: AnalysisBuffer): SharpnessMetrics => {
  const { luma, width, height } = buffer;
  if (width < 3 || height < 3) {
    return { laplacianVariance: 0, tenengrad: 0, highFrequencyRatio: 0 };
  }

  // Ignore a 2 px border to avoid edge-clamping artefacts.
  const laplacianValues: number[] = [];
  const sobelValues: number[] = [];
  for (let y = 2; y < height - 2; y += 1) {
    for (let x = 2; x < width - 2; x += 1) {
      laplacianValues.push(laplacianAt(luma, width, height, x, y));
      sobelValues.push(sobelMagnitudeAt(luma, width, height, x, y));
    }
  }

  const tenengrad = mean(sobelValues.map((value) => value * value));

  const blurred = boxBlur3(luma, width, height);
  let highEnergy = 0;
  let totalEnergy = 0;
  for (let i = 0; i < luma.length; i += 1) {
    const detail = (luma[i] as number) - (blurred[i] as number);
    highEnergy += detail * detail;
    const centred = (luma[i] as number) - 128;
    totalEnergy += centred * centred;
  }

  return {
    laplacianVariance: variance(laplacianValues),
    tenengrad,
    highFrequencyRatio: totalEnergy > 0 ? Math.min(1, highEnergy / totalEnergy) : 0,
  };
};

// ---------------------------------------------------------------------------
// ROI-based sharpness
// ---------------------------------------------------------------------------

/** Reference width (px) each ROI is resampled to before measuring. */
const REFERENCE_WIDTH: Record<RoiRegionDetail['id'], number> = {
  face: 256,
  // The eye band is the most reliable signal, so it gets its own reference.
  eyes: 128,
  nose: 96,
  mouth: 96,
};

const MAX_REFERENCE_HEIGHT = 384;
const MIN_SAMPLE_SIDE = 6;
/** A gradient must exceed this multiple of the noise sigma to count as detail. */
const EDGE_NOISE_K = 3;
/**
 * Absolute gradient floor for the edge-density metric. A large, smooth edge
 * (e.g. the face contour after heavy blur) has a low slope spread over many
 * pixels and must not be mistaken for fine detail.
 */
const EDGE_CONTRAST_FLOOR = 14;
/** Flat, textureless pixels: never let the contrast normaliser collapse to 0. */
const CONTRAST_FLOOR = 1;
/**
 * Absolute floor (luma units) for the high-frequency detail metric. A large,
 * smooth edge has small |luma - boxBlur| and must not be counted as texture.
 */
const HIGH_FREQUENCY_FLOOR = 0.35;
const HIGH_FREQUENCY_CEIL = 3;
/** Share of the local texture amplitude a pixel must exceed to count as detail. */
const HIGH_FREQUENCY_RELATIVE = 0.55;
/** Detail must also clear this multiple of the ROI noise floor. */
const HIGH_FREQUENCY_NOISE_K = 0.6;

const WEIGHT_LAPLACIAN = 0.5;
const WEIGHT_HIGH_FREQUENCY = 0.4;
const WEIGHT_EDGE = 0.1;

/** Region weights when the eye band is available: eyes are the main signal. */
const REGION_WEIGHTS_WITH_EYES: Record<RoiRegionDetail['id'], number> = {
  eyes: 0.5,
  face: 0.3,
  nose: 0.1,
  mouth: 0.1,
};
/** Region weights without eyes: the face carries the verdict. */
const REGION_WEIGHTS_WITHOUT_EYES: Record<RoiRegionDetail['id'], number> = {
  face: 0.7,
  nose: 0.15,
  mouth: 0.15,
  eyes: 0,
};

const logistic = (x: number, center: number, width: number): number => {
  if (!Number.isFinite(x)) return 0;
  const t = clamp((x - center) / width, -30, 30);
  return 1 / (1 + Math.exp(-t));
};

const smoothStep = (value: number, edge0: number, edge1: number): number => {
  if (!Number.isFinite(value)) return 0;
  if (edge1 - edge0 <= 1e-9) return value >= edge1 ? 1 : 0;
  const t = clamp((value - edge0) / (edge1 - edge0), 0, 1);
  return t * t * (3 - 2 * t);
};

interface RoiMeasurements {
  laplacianVariance: number;
  edgeDensity: number;
  /** Mean local (3x3) texture amplitude — used to normalise the Laplacian. */
  localContrast: number;
  /** Spread of luma over the whole ROI — used for the low-contrast note. */
  roiContrast: number;
  highFrequencyRatio: number;
  /** 8x8 block-boundary energy ratio; ~1 when there is no JPEG blockiness. */
  blockiness: number;
  sampleWidth: number;
  sampleHeight: number;
  /** Size of the ROI in *buffer* pixels, before resampling. */
  sourceWidth: number;
  sourceHeight: number;
}

const emptyMeasurements = (): RoiMeasurements => ({
  laplacianVariance: 0,
  edgeDensity: 0,
  localContrast: 0,
  roiContrast: 0,
  highFrequencyRatio: 0,
  blockiness: 1,
  sampleWidth: 0,
  sampleHeight: 0,
  sourceWidth: 0,
  sourceHeight: 0,
});

/**
 * Bilinear resample of one normalized rectangle into a fixed-size float image.
 * Returns `null` when the rectangle (or its intersection with the frame) is too
 * small to measure — callers turn that into an `invalid-region` note.
 */
const resampleRoi = (
  luma: Uint8ClampedArray,
  width: number,
  height: number,
  rect: NormalizedRect,
  targetWidth: number,
): { data: Float32Array; width: number; height: number; sourceWidth: number; sourceHeight: number } | null => {
  if (width < 1 || height < 1) return null;
  if (!Number.isFinite(rect.x) || !Number.isFinite(rect.y)) return null;
  if (!Number.isFinite(rect.width) || !Number.isFinite(rect.height)) return null;
  if (rect.width <= 0 || rect.height <= 0) return null;

  const px0 = Math.round(clamp(rect.x, 0, 1) * width);
  const px1 = Math.round(clamp(rect.x + rect.width, 0, 1) * width);
  const py0 = Math.round(clamp(rect.y, 0, 1) * height);
  const py1 = Math.round(clamp(rect.y + rect.height, 0, 1) * height);
  const sourceWidth = px1 - px0;
  const sourceHeight = py1 - py0;
  if (sourceWidth < MIN_SAMPLE_SIDE || sourceHeight < MIN_SAMPLE_SIDE) return null;

  const outWidth = Math.max(MIN_SAMPLE_SIDE, Math.round(targetWidth));
  const outHeight = clamp(
    Math.round((outWidth * sourceHeight) / sourceWidth),
    MIN_SAMPLE_SIDE,
    MAX_REFERENCE_HEIGHT,
  );

  const data = new Float32Array(outWidth * outHeight);
  const scaleX = sourceWidth / outWidth;
  const scaleY = sourceHeight / outHeight;

  for (let ty = 0; ty < outHeight; ty += 1) {
    const fy = clamp(py0 + (ty + 0.5) * scaleY - 0.5, 0, height - 1);
    const y0 = Math.floor(fy);
    const y1 = Math.min(height - 1, y0 + 1);
    const wy = fy - y0;
    for (let tx = 0; tx < outWidth; tx += 1) {
      const fx = clamp(px0 + (tx + 0.5) * scaleX - 0.5, 0, width - 1);
      const x0 = Math.floor(fx);
      const x1 = Math.min(width - 1, x0 + 1);
      const wx = fx - x0;
      const top = (luma[y0 * width + x0] as number) * (1 - wx) + (luma[y0 * width + x1] as number) * wx;
      const bottom =
        (luma[y1 * width + x0] as number) * (1 - wx) + (luma[y1 * width + x1] as number) * wx;
      data[ty * outWidth + tx] = top * (1 - wy) + bottom * wy;
    }
  }

  return { data, width: outWidth, height: outHeight, sourceWidth, sourceHeight };
};

/**
 * JPEG blockiness of the *source* ROI, before resampling. Resampling destroys
 * the 8x8 grid alignment, so this runs on the analysis pixels directly. Only
 * meaningful at scale 1; a downscaled buffer always returns 1.
 */
const blockinessOfRoi = (
  buffer: AnalysisBuffer,
  rect: NormalizedRect,
): number => {
  const { luma, width, height } = buffer;
  const px0 = Math.max(0, Math.round(clamp(rect.x, 0, 1) * width));
  const px1 = Math.min(width, Math.round(clamp(rect.x + rect.width, 0, 1) * width));
  const py0 = Math.max(0, Math.round(clamp(rect.y, 0, 1) * height));
  const py1 = Math.min(height, Math.round(clamp(rect.y + rect.height, 0, 1) * height));
  if (px1 - px0 < 16 || py1 - py0 < 16) return 1;

  let boundary = 0;
  let boundaryCount = 0;
  let interior = 0;
  let interiorCount = 0;

  for (let y = py0; y < py1; y += 1) {
    for (let x = px0 + 1; x < px1; x += 1) {
      const gradient = Math.abs((luma[y * width + x] as number) - (luma[y * width + x - 1] as number));
      if (x % 8 === 0) {
        boundary += gradient;
        boundaryCount += 1;
      } else {
        interior += gradient;
        interiorCount += 1;
      }
    }
  }
  for (let x = px0; x < px1; x += 1) {
    for (let y = py0 + 1; y < py1; y += 1) {
      const gradient = Math.abs((luma[y * width + x] as number) - (luma[(y - 1) * width + x] as number));
      if (y % 8 === 0) {
        boundary += gradient;
        boundaryCount += 1;
      } else {
        interior += gradient;
        interiorCount += 1;
      }
    }
  }

  const boundaryMean = boundaryCount > 0 ? boundary / boundaryCount : 0;
  const interiorMean = interiorCount > 0 ? interior / interiorCount : 0;
  if (interiorMean < 0.5) return 1;
  return boundaryMean / interiorMean;
};

/** All raw metrics of one resampled ROI. Never throws. */
const measureRoi = (
  sample: { data: Float32Array; width: number; height: number; sourceWidth: number; sourceHeight: number },
  edgeThreshold: number,
  noiseForRoi: number,
  blockiness: number,
): RoiMeasurements => {
  const { data, width, height } = sample;
  if (width < 3 || height < 3) return emptyMeasurements();

  let lapSum = 0;
  let lapSquareSum = 0;
  let lapCount = 0;
  let edgeCount = 0;
  let edgeTotal = 0;
  let localStdSum = 0;
  let localStdCount = 0;
  let lumaSum = 0;
  let lumaSquareSum = 0;
  let lumaCount = 0;

  for (let y = 1; y < height - 1; y += 1) {
    for (let x = 1; x < width - 1; x += 1) {
      const centre = data[y * width + x] as number;
      const up = data[(y - 1) * width + x] as number;
      const down = data[(y + 1) * width + x] as number;
      const left = data[y * width + x - 1] as number;
      const right = data[y * width + x + 1] as number;

      const laplacian = up + down + left + right - 4 * centre;
      lapSum += laplacian;
      lapSquareSum += laplacian * laplacian;
      lapCount += 1;

      const gx =
        (data[(y - 1) * width + x + 1] as number) +
        2 * right +
        (data[(y + 1) * width + x + 1] as number) -
        ((data[(y - 1) * width + x - 1] as number) +
          2 * left +
          (data[(y + 1) * width + x - 1] as number));
      const gy =
        (data[(y + 1) * width + x - 1] as number) +
        2 * down +
        (data[(y + 1) * width + x + 1] as number) -
        ((data[(y - 1) * width + x - 1] as number) +
          2 * up +
          (data[(y - 1) * width + x + 1] as number));
      const gradient = Math.hypot(gx, gy) / 4;
      if (gradient > edgeThreshold) edgeCount += 1;
      edgeTotal += 1;

      // Local texture amplitude: standard deviation of the 3x3 neighbourhood.
      const tl = data[(y - 1) * width + x - 1] as number;
      const tc = up;
      const tr = data[(y - 1) * width + x + 1] as number;
      const ml = left;
      const mr = right;
      const bl = data[(y + 1) * width + x - 1] as number;
      const bc = down;
      const br = data[(y + 1) * width + x + 1] as number;
      const localMean = (tl + tc + tr + ml + centre + mr + bl + bc + br) / 9;
      const localVariance =
        ((tl - localMean) ** 2 +
          (tc - localMean) ** 2 +
          (tr - localMean) ** 2 +
          (ml - localMean) ** 2 +
          (centre - localMean) ** 2 +
          (mr - localMean) ** 2 +
          (bl - localMean) ** 2 +
          (bc - localMean) ** 2 +
          (br - localMean) ** 2) /
        9;
      localStdSum += Math.sqrt(Math.max(0, localVariance));
      localStdCount += 1;
    }
  }

  // Whole-ROI spread: survives blur (large-scale structure) and is what tells a
  // genuinely low-contrast photo from a merely smooth one.
  for (let i = 0; i < data.length; i += 1) {
    const value = data[i] as number;
    lumaSum += value;
    lumaSquareSum += value * value;
    lumaCount += 1;
  }

  const lapMean = lapCount > 0 ? lapSum / lapCount : 0;
  const laplacianVariance = lapCount > 0 ? Math.max(0, lapSquareSum / lapCount - lapMean * lapMean) : 0;
  const localContrast = localStdCount > 0 ? localStdSum / localStdCount : 0;
  const lumaMean = lumaCount > 0 ? lumaSum / lumaCount : 0;
  const roiContrast = lumaCount > 0 ? Math.sqrt(Math.max(0, lumaSquareSum / lumaCount - lumaMean * lumaMean)) : 0;

  // High-frequency detail: how much of the ROI carries texture above the
  // 3x3-box-blur reference. A *density* (share of pixels) is used instead of a
  // plain energy ratio, because a single strong contour makes an energy ratio
  // saturate even when the rest of the ROI is completely flat. The threshold is
  // tied to the ROI's own texture amplitude (and the noise floor), which keeps
  // the metric stable when the analysis buffer is downscaled and the ROI is
  // scaled back up.
  const highFrequencyThreshold = clamp(
    Math.max(HIGH_FREQUENCY_RELATIVE * localContrast, HIGH_FREQUENCY_NOISE_K * noiseForRoi),
    HIGH_FREQUENCY_FLOOR,
    HIGH_FREQUENCY_CEIL,
  );
  const blurred = boxBlur3(data, width, height);
  let highFrequencyCount = 0;
  for (let i = 0; i < data.length; i += 1) {
    const detail = Math.abs((data[i] as number) - (blurred[i] as number));
    if (detail > highFrequencyThreshold) highFrequencyCount += 1;
  }
  const highFrequencyRatio = data.length > 0 ? highFrequencyCount / data.length : 0;

  return {
    laplacianVariance,
    edgeDensity: edgeTotal > 0 ? edgeCount / edgeTotal : 0,
    localContrast,
    roiContrast,
    highFrequencyRatio,
    blockiness,
    sampleWidth: width,
    sampleHeight: height,
    sourceWidth: sample.sourceWidth,
    sourceHeight: sample.sourceHeight,
  };
};

/** Maps the raw metrics of one ROI into a single 0..1 detail value. */
const detailOf = (measurements: RoiMeasurements): number => {
  const contrastReference = Math.max(measurements.localContrast, CONTRAST_FLOOR);
  const normalizedLaplacian = measurements.laplacianVariance / (contrastReference * contrastReference);

  // Log-domain logistic: raw Laplacian energy spans several orders of magnitude.
  const laplacianScore = logistic(Math.log10(normalizedLaplacian + 1e-6), Math.log10(5), 0.34);
  const highFrequencyScore = smoothStep(measurements.highFrequencyRatio, 0.12, 0.4);
  const edgeScore = smoothStep(measurements.edgeDensity, 0.008, 0.06);

  const detail =
    WEIGHT_LAPLACIAN * laplacianScore +
    WEIGHT_HIGH_FREQUENCY * highFrequencyScore +
    WEIGHT_EDGE * edgeScore;

  return clamp(detail, 0, 1);
};

const isFiniteRect = (rect: NormalizedRect): boolean =>
  Number.isFinite(rect.x) &&
  Number.isFinite(rect.y) &&
  Number.isFinite(rect.width) &&
  Number.isFinite(rect.height);

/**
 * Face-aware, scale-invariant sharpness.
 *
 * `regions` are normalized rectangles; each is resampled to its reference width
 * before any metric is computed, so the result does not depend on the analysis
 * buffer scale or on how much of the frame the face occupies.
 */
export const computeRoiSharpness = (input: {
  buffer: AnalysisBuffer;
  regions: Array<{ id: RoiRegionDetail['id']; rect: NormalizedRect }>;
  noiseSigma?: number;
}): RoiSharpness => {
  const { buffer } = input;
  const regions = Array.isArray(input.regions) ? input.regions : [];
  const notes: string[] = [];

  const bufferUsable =
    !!buffer &&
    Number.isFinite(buffer.width) &&
    Number.isFinite(buffer.height) &&
    buffer.width >= MIN_SAMPLE_SIDE &&
    buffer.height >= MIN_SAMPLE_SIDE &&
    buffer.luma instanceof Uint8ClampedArray &&
    buffer.luma.length >= buffer.width * buffer.height;

  if (!bufferUsable) {
    return { regions: [], score: 0, confidence: 0, notes: ['invalid-region'], normalizedScale: 1 };
  }

  const noiseSigma = Number.isFinite(input.noiseSigma)
    ? Math.max(0, input.noiseSigma as number)
    : estimateNoiseSigma(buffer);

  const details: RoiRegionDetail[] = [];
  const measurementsById = new Map<RoiRegionDetail['id'], RoiMeasurements>();
  const scalesById = new Map<RoiRegionDetail['id'], number>();
  let anyInvalid = false;

  for (const region of regions) {
    const validRegion = region && isFiniteRect(region.rect);
    const referenceWidth = REFERENCE_WIDTH[region?.id] ?? 128;
    if (!validRegion) {
      anyInvalid = true;
      details.push({
        id: region?.id ?? 'face',
        detail: 0,
        laplacianVariance: 0,
        edgeDensity: 0,
        localContrast: 0,
        sampleSize: { width: 0, height: 0 },
      });
      continue;
    }

    const sample = resampleRoi(buffer.luma, buffer.width, buffer.height, region.rect, referenceWidth);
    if (!sample) {
      anyInvalid = true;
      details.push({
        id: region.id,
        detail: 0,
        laplacianVariance: 0,
        edgeDensity: 0,
        localContrast: 0,
        sampleSize: { width: 0, height: 0 },
      });
      continue;
    }

    const normalizedScale = sample.sourceWidth > 0 ? sample.width / sample.sourceWidth : 1;
    const noiseForRoi = noiseSigma * Math.min(1, normalizedScale);
    const edgeThreshold = Math.max(EDGE_NOISE_K * noiseForRoi, EDGE_CONTRAST_FLOOR);
    const blockiness = buffer.scale >= 0.999 ? blockinessOfRoi(buffer, region.rect) : 1;
    const measurements = measureRoi(sample, edgeThreshold, noiseForRoi, blockiness);
    measurementsById.set(region.id, measurements);
    scalesById.set(region.id, normalizedScale);

    details.push({
      id: region.id,
      detail: detailOf(measurements),
      laplacianVariance: measurements.laplacianVariance,
      edgeDensity: measurements.edgeDensity,
      localContrast: measurements.localContrast,
      sampleSize: { width: measurements.sampleWidth, height: measurements.sampleHeight },
    });
  }

  if (anyInvalid) notes.push('invalid-region');

  const withEyes = details.some((region) => region.id === 'eyes' && region.sampleSize.width > 0);
  const weights = withEyes ? REGION_WEIGHTS_WITH_EYES : REGION_WEIGHTS_WITHOUT_EYES;
  let weightSum = 0;
  let weightedDetail = 0;
  for (const region of details) {
    if (region.sampleSize.width <= 0) continue;
    const weight = weights[region.id] ?? 0;
    if (weight <= 0) continue;
    weightSum += weight;
    weightedDetail += weight * region.detail;
  }
  const score = weightSum > 0 ? clamp(weightedDetail / weightSum, 0, 1) : 0;

  // ------------------------------------------------------------- confidence
  let confidence = weightSum > 0 ? 1 : 0;

  // The primary region sets the confidence: the face box when present, else the
  // largest valid region.
  const primary =
    details.find((region) => region.id === 'face' && region.sampleSize.width > 0) ??
    details
      .filter((region) => region.sampleSize.width > 0)
      .sort((a, b) => b.sampleSize.width * b.sampleSize.height - a.sampleSize.width * a.sampleSize.height)[0];
  const primaryMeasurements = primary ? measurementsById.get(primary.id) : undefined;

  // Buffer-space size of the face: a 40 px face cannot support a confident verdict.
  const primaryBufferMinSide = primaryMeasurements
    ? Math.min(primaryMeasurements.sourceWidth, primaryMeasurements.sourceHeight)
    : 0;
  if (primary && primaryBufferMinSide < 64) {
    notes.push('face-too-small');
    confidence *= clamp((primaryBufferMinSide - 20) / (64 - 20), 0.15, 1);
  }

  if (primaryMeasurements && primaryMeasurements.roiContrast < 14) {
    notes.push('low-contrast');
    confidence *= clamp(primaryMeasurements.roiContrast / 14, 0.2, 1);
  }

  if (noiseSigma > 12) {
    notes.push('high-noise');
    confidence *= clamp(1 - (noiseSigma - 12) / 60, 0.35, 1);
  }

  const normalizedScale = primary ? scalesById.get(primary.id) ?? 1 : 1;
  if (normalizedScale > 1.25) {
    notes.push('upscaled');
    confidence *= clamp(1 - (normalizedScale - 1.25) * 0.3, 0.45, 1);
  }

  if (primaryMeasurements && primaryMeasurements.blockiness > 1.3) {
    notes.push('jpeg-artifacts');
    confidence *= 0.85;
  }

  return {
    regions: details,
    score: Number.isFinite(score) ? clamp(score, 0, 1) : 0,
    confidence: clamp(confidence, 0, 1),
    notes: [...new Set(notes)],
    normalizedScale: Number.isFinite(normalizedScale) ? normalizedScale : 1,
  };
};

/**
 * Cheap fallback noise estimate for the ROI edge threshold: the Laplacian of
 * white noise has variance `20 * sigma^2`, so its MAD converts back to sigma.
 */
const estimateNoiseSigma = (buffer: AnalysisBuffer): number => {
  const { luma, width, height } = buffer;
  if (width < 3 || height < 3) return 0;
  const values: number[] = [];
  const step = Math.max(1, Math.floor(Math.sqrt((width * height) / 40000)));
  for (let y = 1; y < height - 1; y += step) {
    for (let x = 1; x < width - 1; x += step) {
      values.push(laplacianAt(luma, width, height, x, y));
    }
  }
  if (values.length === 0) return 0;
  values.sort((a, b) => a - b);
  const median = values[Math.floor(values.length / 2)] as number;
  const deviations = values.map((value) => Math.abs(value - median)).sort((a, b) => a - b);
  const mad = deviations[Math.floor(deviations.length / 2)] as number;
  return (mad * 1.4826) / Math.sqrt(20);
};
